import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Container, Table, Button, Alert, Input, Row, Col } from 'reactstrap';
import {
  getComments,
  createComment,
  updateComment,
  deleteCommentById,
  setCreateComment,
  setUpdateComment,
  setDeleteCommentById,
} from '../../redux/actions/comment';

import Loading from '../../components/Loading/Loading';
import ConfirmationModal from '../../components/Modal/ConfirmationModal';
import CommentModal from './subcomponents/CommentModal';
import styles from './styles.module.scss';
import { hasPermission } from '../../utils/permissions';

// Comment sections options
const COMMENT_SECTIONS = [
  { value: '', label: 'Todas las secciones' },
  { value: 'car_inspection', label: 'Inspección de carroceria' },
  { value: 'accesories_inventory', label: 'Accesories e Inventario' },
  { value: 'mechanic_state', label: 'Estado mecánico' },
  { value: 'security', label: 'Seguridad' },
];

function CommentList() {
  const dispatch = useDispatch();
  const listState = useSelector((state) => state.comment.get);
  const createState = useSelector((state) => state.comment.create);
  const updateState = useSelector((state) => state.comment.update);
  const deleteState = useSelector((state) => state.comment.deleteById);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('');
  const [showAlert, setShowAlert] = useState(false);
  const [selectedSection, setSelectedSection] = useState('');
  const [searchValue, setSearchValue] = useState('');
  // Modal states
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingComment, setEditingComment] = useState(null);

  const refresh = useCallback(() => {
    setLoading(true);
    dispatch(getComments(selectedSection, searchValue));
  }, [dispatch, selectedSection, searchValue]);

  const handleCreate = useCallback(
    (value, section) => {
      setLoading(true);
      dispatch(createComment(value, section));
    },
    [dispatch],
  );

  const handleEdit = useCallback(
    (id, value, section) => {
      setLoading(true);
      dispatch(updateComment(id, value, section));
    },
    [dispatch],
  );

  const handleDelete = useCallback(
    (id) => {
      setLoading(true);
      dispatch(deleteCommentById(id));
    },
    [dispatch],
  );

  // Modal control methods
  const openCreateModal = () => {
    setIsCreateModalOpen(true);
  };

  const closeCreateModal = () => {
    setIsCreateModalOpen(false);
  };

  const openEditModal = (comment) => {
    setEditingComment(comment);
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditingComment(null);
    setIsEditModalOpen(false);
  };

  const onCommentCreate = (value, section) => {
    handleCreate(value, section);
    closeCreateModal();
  };

  const onCommentEdit = (value, section) => {
    if (editingComment) {
      handleEdit(editingComment.id, value, section);
      closeEditModal();
    }
  };

  const handleAlert = (type, message) => {
    setAlertType(type);
    setAlertMessage(message);
    setShowAlert(true);
    const timer = setTimeout(() => setShowAlert(false), 3000);
    return () => clearTimeout(timer);
  };

  const clearReduxState = useCallback(
    (actionCreator, delay = 3500) => {
      setTimeout(() => {
        dispatch(actionCreator(null));
      }, delay);
    },
    [dispatch],
  );

  const convertSection = (section) => {
    switch (section) {
      case 'car_inspection':
        return 'Inspección de carroceria';
      case 'accesories_inventory':
        return 'Accesories e Inventario';
      case 'mechanic_state':
        return 'Estado mecánico';
      case 'security':
        return 'Seguridad';
      default:
        return '--';
    }
  };

  // Handle section filter change
  const handleSectionFilter = (e) => {
    setSelectedSection(e.target.value);
    setLoading(true);
    dispatch(getComments(e.target.value, searchValue));
  };

  // Handle search value change
  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
  };

  // Handle search submit (when user presses Enter or clicks search)
  const handleSearch = () => {
    setLoading(true);
    dispatch(getComments(selectedSection, searchValue));
  };

  // Handle clear search
  const handleClearSearch = () => {
    setSearchValue('');
    setLoading(true);
    dispatch(getComments(selectedSection, ''));
  };

  useEffect(() => {
    document.title = 'BGL - Comentarios';
    // Solo carga inicial
    setLoading(true);
    dispatch(getComments());
  }, [dispatch]);

  // Efecto separado para manejar respuestas de acciones
  useEffect(() => {
    if (listState) {
      setData(listState.data || []);
      setLoading(false);
    }
  }, [listState]);

  useEffect(() => {
    if (createState) {
      handleAlert(createState.status, createState.message);
      setLoading(false);
      // Solo refresca si fue exitoso
      if (createState.status === 'success') {
        dispatch(getComments());
      }
      clearReduxState(setCreateComment);
    }
  }, [createState, clearReduxState, dispatch]);

  useEffect(() => {
    if (updateState) {
      handleAlert(updateState.status, updateState.message);
      setLoading(false);
      // Solo refresca si fue exitoso
      if (updateState.status === 'success') {
        dispatch(getComments());
      }
      clearReduxState(setUpdateComment);
    }
  }, [updateState, clearReduxState, dispatch]);

  useEffect(() => {
    if (deleteState) {
      handleAlert(deleteState.status, deleteState.message);
      setLoading(false);
      // Solo refresca si fue exitoso
      if (deleteState.status === 'success') {
        dispatch(getComments());
      }
      clearReduxState(setDeleteCommentById);
    }
  }, [deleteState, clearReduxState, dispatch]);

  return loading ? (
    <Loading />
  ) : (
    <>
      <Container>
        <Alert
          color={alertType}
          isOpen={showAlert}
          toggle={() => setShowAlert(false)}
        >
          {alertMessage}
        </Alert>
      </Container>

      <Container className="container-border p-3">
        <article>
          {hasPermission('comments.create') && (
            <Button
              className="float-right mb-2"
              size="sm"
              onClick={openCreateModal}
            >
              <i className="fa fa-plus mr-3"></i>
              <span>Crear comentario</span>
            </Button>
          )}
          <Button
            className="float-right mb-2 mr-2"
            size="sm"
            onClick={() => refresh()}
          >
            <i className="fa fa-sync mr-3"></i>
            <span>Refrescar</span>
          </Button>
          <h2>Lista de Comentarios</h2>
        </article>

        <Row className="mb-3">
          <Col>
            <Input
              id="sectionFilter"
              bsSize="sm"
              type="select"
              className={styles.sectionSelect}
              value={selectedSection}
              onChange={handleSectionFilter}
            >
              {COMMENT_SECTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Input>
          </Col>
          <Col>
            <Input
              id="searchValue"
              bsSize="sm"
              type="search"
              className="m-0"
              placeholder="Buscar comentario por valor"
              value={searchValue}
              onChange={handleSearchChange}
            />
          </Col>
          <Col className="p-0">
            <Button
              type="button"
              color="primary"
              size="sm"
              className="m-0"
              onClick={() => handleSearch()}
            >
              <i className="fa fa-search"></i>
            </Button>
            <Button
              type="button"
              color="primary"
              size="sm"
              className="m-0 ml-2"
              disabled={searchValue === ''}
              onClick={() => handleClearSearch()}
            >
              <i className="fa fa-times"></i>
            </Button>
          </Col>
        </Row>

        <Table hover>
          <thead>
            <tr>
              <th>ID</th>
              <th>Sección</th>
              <th>Valor</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {data.length === 0 && (
              <tr>
                <td colSpan="4" className="text-center">
                  {selectedSection && searchValue
                    ? 'No hay comentarios que coincidan con la sección y búsqueda seleccionadas'
                    : selectedSection
                    ? 'No hay comentarios disponibles para la sección seleccionada'
                    : searchValue
                    ? 'No hay comentarios que coincidan con la búsqueda'
                    : 'No hay comentarios disponibles'}
                </td>
              </tr>
            )}

            {data.map((row) => {
              return (
                <tr key={row.id}>
                  <td>{row.id}</td>
                  <td>{convertSection(row.section)}</td>
                  <td>{row.value}</td>
                  <td>
                    <>
                      {hasPermission('comments.edit') && (
                        <Button
                          className="mr-2"
                          size="sm"
                          onClick={() => openEditModal(row)}
                        >
                          <i className="fa fa-edit"></i>
                        </Button>
                      )}
                      {hasPermission('comments.delete') && (
                        <ConfirmationModal
                          btnClass="btn btn-danger btn-sm"
                          iconClass="fa fa-trash"
                          title="Eliminar comentario"
                          message="¿Estas seguro que quieres eliminar este comentario? Esta operación no se podrá deshacer."
                          action={() => handleDelete(row.id)}
                          argument={row.id}
                          successMessage="Eliminar"
                          cancelMessage="Cancelar"
                          btnSuccessState="danger"
                          btnCancelState="secondary"
                        />
                      )}
                    </>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </Container>

      {/* Create Comment Modal */}
      <CommentModal
        isOpen={isCreateModalOpen}
        onToggle={closeCreateModal}
        title="Crear comentario"
        onSubmit={onCommentCreate}
      />

      {/* Edit Comment Modal */}
      <CommentModal
        isOpen={isEditModalOpen}
        onToggle={closeEditModal}
        title="Editar comentario"
        defaultValue={editingComment?.value || ''}
        defaultSection={editingComment?.section || ''}
        onSubmit={onCommentEdit}
      />
    </>
  );
}

export default CommentList;
